import {Link} from "react-router-dom";
import {useQuery} from "@tanstack/react-query";
import {buttonVariants} from "@/components/ui/button.tsx";
import {Skeleton} from "@/components/ui/skeleton.tsx";
import {queryKeys} from "@/services/queryKeyFactory.ts";
import {getMentorById, getMentorProfileCompletionStats} from "@/services/mentorService.ts";
import {useTranslation} from "react-i18next";
import MentorSessionsBlock from "@/pages/dashboard/mentor/MentorSessionsBlock.tsx";
import { DashboardStatsChart } from "@/components/DashboardStatsChart.tsx";
import {Calendar, ClipboardList, UserPlus, Users} from "lucide-react";
import { PageTitle, BlockTitle } from "@/components/ui/headings";

type MentorDashboardProps = {
    mentorId: number,
    submitPreferenceDeadline: Date | undefined,
    selectMentorDeadline: Date | undefined,
}

const MentorDashboardPage = ({
                                 mentorId,
                                 submitPreferenceDeadline,
                                 selectMentorDeadline
                             }: MentorDashboardProps) => {
    const {t} = useTranslation();

    const {data: mentor, isFetching} = useQuery({
        queryKey: queryKeys.fetchMentors.one,
        queryFn: () => getMentorById(mentorId),
        initialData: null,
    });

    const {data: completionStats} = useQuery({
        queryKey: queryKeys.fetchMentors.profileCompletionStats,
        queryFn: getMentorProfileCompletionStats,
    });

    const today = new Date();

    if (isFetching || !mentor) {
        return (
            <div className="flex flex-col space-y-3 ml-6">
                <div className="space-y-2">
                    <Skeleton className="h-8 w-full"/>
                    <Skeleton className="h-8 w-full"/>
                    <Skeleton className="h-8 w-full"/>
                    <Skeleton className="h-8 w-full"/>
                </div>
            </div>
        )
    }

    return (
        <div className="container pb-12">
            {!selectMentorDeadline || today <= selectMentorDeadline ? (
                <div className="py-12 grid gap-8 lg:grid-cols-[1fr,340px] lg:items-start">
                    <div className="space-y-6">
                        <div className="rounded-xl border border-border bg-bg-card p-8 shadow-sm">
                            <PageTitle>
                                {mentor.languages && mentor.languages.length > 0
                                    ? t("dashboard.thanksForProfileSubmission")
                                    : t("dashboard.promptToUpdateProfile", {deadline: submitPreferenceDeadline?.toDateString()})}
                            </PageTitle>
                            <p className="mt-3 text-base text-text-2">
                                {t("dashboard.willNotifyYou", {deadline: selectMentorDeadline?.toDateString()})}
                            </p>

                            {!mentor.languages || mentor.languages.length === 0 ? (
                                <Link className={`${buttonVariants({variant: "default"})} mt-6 inline-block`} to={`/mentors/${mentorId}`}>
                                    {t("profile.updateProfile")}
                                </Link>
                            ) : null}
                        </div>

                        {completionStats && (
                            <DashboardStatsChart
                                data={{
                                    completedCount: completionStats.completedCount,
                                    totalCount: completionStats.totalMentors,
                                    percentChangeToday: completionStats.percentChangeToday,
                                    timeSeries: completionStats.timeSeries,
                                }}
                                statDescriptionKey="dashboard.mentorsCompletedProfiles"
                                totalLabelKey="dashboard.outOfTotalMentors"
                                chartLabelKey="dashboard.profileCompletionsOverTime"
                            />
                        )}
                    </div>

                    {(submitPreferenceDeadline != null || selectMentorDeadline != null) && (
                        <div className="rounded-xl border border-border bg-bg-card p-6 shadow-sm">
                            <BlockTitle>{t("dashboard.timeline.title", "Timeline")}</BlockTitle>
                            <ul className="mt-4 space-y-0">
                                {[
                                    {
                                        date: submitPreferenceDeadline,
                                        label: t("dashboard.timeline.lastDayProfile", "Last day to complete mentorship profile"),
                                        Icon: ClipboardList,
                                    },
                                    {
                                        date: submitPreferenceDeadline
                                            ? new Date(submitPreferenceDeadline.getTime() + 3 * 24 * 60 * 60 * 1000)
                                            : undefined,
                                        label: t("dashboard.timeline.matchingStarts", "Matching process starts"),
                                        Icon: Users,
                                    },
                                    {
                                        date: selectMentorDeadline,
                                        label: t("dashboard.timeline.lastDayJoinGroup", "Last day to join a mentorship group"),
                                        Icon: UserPlus,
                                    },
                                    {
                                        date: selectMentorDeadline
                                            ? new Date(selectMentorDeadline.getTime() + 7 * 24 * 60 * 60 * 1000)
                                            : undefined,
                                        label: t("dashboard.timeline.seasonStarts", "Mentorship season starts"),
                                        Icon: Calendar,
                                    },
                                ].map(({ date, label, Icon }, i) => (
                                    <li key={i} className="relative flex gap-4 pb-6 last:pb-0">
                                        {i < 3 && (
                                            <span
                                                className="absolute left-[7px] top-8 bottom-0 w-px bg-border"
                                                aria-hidden
                                            />
                                        )}
                                        <span className="relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 border-border bg-bg-card text-text-2">
                                            <Icon className="h-4 w-4" aria-hidden />
                                        </span>
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-medium text-text-3">
                                                {date ? date.toLocaleDateString(undefined, { dateStyle: "medium" }) : "—"}
                                            </p>
                                            <p className="mt-0.5 text-base font-medium text-text-1">{label}</p>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            ) : null}

            {selectMentorDeadline && today > selectMentorDeadline && mentor ? (
                <div className="pt-8">
                    <MentorSessionsBlock mentor={mentor} />
                </div>
            ) : null}
        </div>
    )
}

export default MentorDashboardPage;
